import React, { Suspense, useState, useEffect, useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Stage, useGLTF, Environment, ContactShadows, Bounds, PresentationControls } from '@react-three/drei';
import { X, ExternalLink, Maximize2, Minimize2, RotateCw, Pause, Eye, EyeOff, Component as CompIcon, FolderGit2, Layers, Search, SlidersHorizontal, ChevronRight, ChevronDown, Move3d, Box } from 'lucide-react';
import * as THREE from 'three';
import { API_BASE_URL } from '../utils/apiConfig';

interface CadViewerModalProps {
    isOpen: boolean;
    onClose: () => void;
    synthesisId: string;
    projectName: string;
}

// ── Disaggregated Node Renderer ──────────────────────────────────────
const IndividualMesh = ({ 
    node, 
    meshUrl, 
    isExploded, 
    visible,
    selectedNode,
    hoveredNode,
    onHover,
    onClick
}: { 
    node: any, 
    meshUrl: string, 
    isExploded: boolean, 
    visible: boolean,
    selectedNode: string | null,
    hoveredNode: string | null,
    onHover: (id: string | null) => void,
    onClick: (id: string) => void
}) => {
    const { scene } = useGLTF(meshUrl);
    const meshRef = useRef<THREE.Group>(null);
    const clone = useMemo(() => scene.clone(), [scene]);
    
    // Original constrained JSON position
    const origPos = useMemo(() => new THREE.Vector3(...(node.kinematics?.position || [0,0,0])), [node]);
    
    // Dynamic Explosion logic relying on kinematics vectors or Origin-Radial
    const explodeVector = useMemo(() => {
        if (node.kinematics?.explode_vector) {
            return new THREE.Vector3(...node.kinematics.explode_vector).normalize();
        }
        return origPos.clone().normalize();
    }, [node, origPos]);

    const explodeDist = 3.5;
    const targetPos = useMemo(() => isExploded ? origPos.clone().add(explodeVector.multiplyScalar(explodeDist)) : origPos, [isExploded, origPos, explodeVector]);

    const isSelected = selectedNode === node.id;
    const isHovered = hoveredNode === node.id;
    const isUnfocused = selectedNode && !isSelected;

    useFrame((state, delta) => {
        if (meshRef.current) {
            meshRef.current.position.lerp(targetPos, 0.15);
            const targetScale = isSelected ? 1.02 : 1.0;
            meshRef.current.scale.lerp(new THREE.Vector3(targetScale, targetScale, targetScale), 0.15);
        }
    });

    // Clone materials once per instance to avoid sharing
    useEffect(() => {
        clone.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                if (!child.userData.hasUniqueMaterial) {
                    child.material = (child.material as THREE.Material).clone();
                    child.userData.hasUniqueMaterial = true;
                }
            }
        });

        // Gap 3.2a: Dispose GPU resources on unmount to prevent memory leaks
        return () => {
            clone.traverse((child) => {
                if (child instanceof THREE.Mesh) {
                    child.geometry?.dispose();
                    if (child.material) {
                        const mat = child.material as THREE.MeshStandardMaterial;
                        mat.map?.dispose();
                        mat.normalMap?.dispose();
                        mat.roughnessMap?.dispose();
                        mat.metalnessMap?.dispose();
                        mat.dispose();
                    }
                }
            });
        };
    }, [clone]);

    // PBR Material Overrides based on state
    useEffect(() => {
        clone.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                const newMat = child.material as THREE.MeshStandardMaterial;
                
                newMat.roughness = 0.3;
                newMat.metalness = 0.8;
                
                if (isSelected) {
                    newMat.emissive = new THREE.Color('#3b82f6');
                    newMat.emissiveIntensity = 0.5;
                    newMat.transparent = false;
                    newMat.opacity = 1;
                    newMat.depthWrite = true;
                } else if (isHovered) {
                    newMat.emissive = new THREE.Color('#60a5fa');
                    newMat.emissiveIntensity = 0.2;
                    newMat.transparent = false;
                    newMat.opacity = 1;
                    newMat.depthWrite = true;
                } else if (isUnfocused) {
                    newMat.emissive = new THREE.Color(0x000000);
                    newMat.emissiveIntensity = 0;
                    newMat.transparent = true;
                    newMat.opacity = 0.2;
                    newMat.depthWrite = false;
                } else {
                    newMat.emissive = new THREE.Color(0x000000);
                    newMat.emissiveIntensity = 0;
                    newMat.transparent = false;
                    newMat.opacity = 1;
                    newMat.depthWrite = true;
                }
            }
        });
    }, [clone, isSelected, isHovered, isUnfocused]);

    if (!visible) return null;
    return (
        <primitive 
            object={clone} 
            ref={meshRef} 
            position={origPos}
            onPointerOver={(e: any) => { e.stopPropagation(); onHover(node.id); }}
            onPointerOut={() => onHover(null)}
            onClick={(e: any) => { e.stopPropagation(); onClick(node.id); }}
        />
    );
};

// ── Recursive Indented BOM Tree ──────────────────────────────────────
const TreeNode = ({ node, nodes, hiddenNodes, toggleNodeVisibility, selectedNode, hoveredNode, setSelectedNode, setHoveredNode, level = 0 }: any) => {
    const isHidden = hiddenNodes.has(node.id);
    const isSelected = selectedNode === node.id;
    const isHovered = hoveredNode === node.id;
    const [expanded, setExpanded] = useState(level < 2);
    
    // Find virtual children either by parentId or mock cluster
    const children = nodes.filter((n: any) => n.parentId === node.id);
    const hasChildren = children.length > 0;

    return (
        <div className="w-full">
            <div 
                className={`flex items-center justify-between py-2 px-2 mb-0.5 rounded-lg cursor-pointer transition-all border ${
                    isSelected ? 'bg-blue-500/20 border-blue-500/40 text-blue-100 shadow-[inset_0_0_12px_rgba(59,130,246,0.2)]' : 
                    isHovered ? 'bg-white/5 border-white/10 text-white' : 
                    isHidden ? 'opacity-50 border-transparent text-slate-400' : 'border-transparent text-slate-300 hover:bg-white/5'
                }`}
                style={{ paddingLeft: `${level * 16 + 8}px` }}
                onMouseEnter={() => setHoveredNode(node.id)}
                onMouseLeave={() => setHoveredNode(null)}
                onClick={() => setSelectedNode(isSelected ? null : node.id)}
            >
                <div className="flex items-center gap-2 overflow-hidden">
                    {hasChildren ? (
                        <button onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }} className="p-0.5 hover:bg-white/10 rounded text-slate-400">
                            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                    ) : <span className="w-4" />}
                    <CompIcon size={14} className={isSelected ? 'text-blue-400' : 'text-slate-500'} />
                    <span className="truncate text-sm tracking-wide font-medium">{node.name}</span>
                </div>
                <button 
                    onClick={(e) => { 
                        e.stopPropagation(); 
                        toggleNodeVisibility(node.id);
                    }}
                    className="p-1.5 hover:bg-white/10 rounded-md text-slate-400 hover:text-white transition-colors"
                >
                    {isHidden ? <EyeOff size={14} className="text-red-400" /> : <Eye size={14} />}
                </button>
            </div>
            {expanded && hasChildren && (
                <div className="flex flex-col w-full">
                    {children.map((child: any) => (
                        <TreeNode 
                            key={child.id} 
                            node={child} 
                            nodes={nodes} 
                            hiddenNodes={hiddenNodes} 
                            toggleNodeVisibility={toggleNodeVisibility}
                            selectedNode={selectedNode}
                            hoveredNode={hoveredNode}
                            setSelectedNode={setSelectedNode}
                            setHoveredNode={setHoveredNode}
                            level={level + 1} 
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const CadViewerModal: React.FC<CadViewerModalProps> = ({ isOpen, onClose, synthesisId, projectName }) => {
    const [isExploded, setIsExploded] = useState(false);
    const [autoRotate, setAutoRotate] = useState(true);
    const [manifest, setManifest] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    
    // UI State
    const [hiddenNodes, setHiddenNodes] = useState<Set<string>>(new Set());
    const [selectedNode, setSelectedNode] = useState<string | null>(null);
    const [hoveredNode, setHoveredNode] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState("");

    // Fetch the JSON Blueprint
    useEffect(() => {
        if (!isOpen || !synthesisId) return;
        setLoading(true);
        fetch(`${API_BASE_URL}/api/v1/genesis/synthesis/${synthesisId}/cad-manifest`)
            .then(res => {
                if (!res.ok) throw new Error("Manifest not found (M x N guard may have triggered)");
                return res.json();
            })
            .then(data => {
                setManifest(data);
                setHiddenNodes(new Set());
            })
            .catch(err => {
                console.error("CAD load error:", err);
                setManifest({ error: true, details: err.message });
            })
            .finally(() => setLoading(false));
    }, [isOpen, synthesisId]);

    // Reset view and dispose cached GLTFs on modal close (Gap 3.2a)
    useEffect(() => {
        if (!isOpen) {
            setIsExploded(false);
            setAutoRotate(true);
            setHiddenNodes(new Set());
            setSelectedNode(null);
            setHoveredNode(null);
            setSearchQuery("");
            setManifest(null);
            // Flush useGLTF cache to free GPU memory
            try { useGLTF.clear(); } catch (_) {}
        }
    }, [isOpen]);

    const toggleNodeVisibility = (nodeId: string, forceHidden?: boolean) => {
        setHiddenNodes(prev => {
            const next = new Set(prev);
            const willHide = forceHidden !== undefined ? forceHidden : !next.has(nodeId);
            
            const toggleDeep = (id: string, hide: boolean) => {
                if (hide) next.add(id);
                else next.delete(id);
                
                const children = manifest?.nodes?.filter((n: any) => n.parentId === id) || [];
                children.forEach((child: any) => toggleDeep(child.id, hide));
            };
            
            toggleDeep(nodeId, willHide);
            return next;
        });
    };

    if (!isOpen) return null;

    const hasError = manifest?.error;

    // Filter root nodes (those without parentId or whose parent doesn't exist)
    // If no hierarchy is defined in data, all nodes will be root nodes.
    const nodesArray = manifest?.nodes || [];
    const rootNodes = nodesArray.filter((n: any) => !n.parentId || !nodesArray.find((parent: any) => parent.id === n.parentId));

    const displayedNodes = searchQuery 
        ? nodesArray.filter((n: any) => n.name.toLowerCase().includes(searchQuery.toLowerCase()))
        : rootNodes;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Ultra-premium glass backdrop */}
            <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-md" onClick={onClose} />
            
            <div className="relative z-10 bg-[#0a0f1c]/90 border border-slate-700/50 rounded-2xl shadow-2xl shadow-blue-500/10 w-full max-w-[95vw] h-[92vh] flex flex-col overflow-hidden backdrop-blur-xl">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800/80 bg-slate-900/40">
                    <div className="flex items-center gap-4">
                        <div className="p-2.5 bg-blue-600 rounded-xl shadow-lg shadow-blue-600/30">
                            <Box size={24} className="text-white" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-black text-white flex items-center gap-3 tracking-wide">
                                MAGNUS ASSEMBLY STUDIO
                                {isExploded && <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-500 uppercase tracking-widest border border-amber-500/30 shadow-[0_0_10px_rgba(245,158,11,0.2)]">Exploded View</span>}
                            </h2>
                            <p className="text-xs text-blue-400 font-mono tracking-widest uppercase mt-1">Mechatronic Manifest &mdash; {projectName}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5 bg-slate-900/80 p-1.5 rounded-xl border border-slate-700/50 shadow-inner">
                            <button
                                onClick={() => setIsExploded(!isExploded)}
                                disabled={hasError}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all text-sm font-bold uppercase tracking-wider ${isExploded ? 'bg-amber-500 text-white shadow-[0_0_15px_rgba(245,158,11,0.4)]' : 'hover:bg-slate-800 text-slate-300 hover:text-white'} disabled:opacity-50`}
                            >
                                {isExploded ? <Minimize2 size={16} /> : <Maximize2 size={16} />} 
                                {isExploded ? 'Collapse' : 'Explode'}
                            </button>
                            <button
                                onClick={() => setAutoRotate(!autoRotate)}
                                disabled={hasError}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all text-sm font-bold uppercase tracking-wider ${autoRotate ? 'bg-blue-600 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)]' : 'hover:bg-slate-800 text-slate-300 hover:text-white'} disabled:opacity-50`}
                            >
                                {autoRotate ? <Pause size={16} /> : <RotateCw size={16} />} 
                                {autoRotate ? 'Stop Spin' : 'Turntable'}
                            </button>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-3 text-slate-400 hover:text-white hover:bg-red-500/20 hover:border-red-500/40 border border-transparent rounded-xl transition-all"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Main Split Layout */}
                <div className="flex flex-1 overflow-hidden relative">
                    
                    {/* Left Pane: Enterprise Indented BOM */}
                    <div className="w-[380px] bg-[#05080f]/80 flex flex-col border-r border-slate-800/80 shadow-2xl relative z-10 backdrop-blur-md">
                        {/* Search / Filter Bar */}
                        <div className="p-4 border-b border-slate-800/80 bg-slate-900/30">
                            <div className="relative">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                <input 
                                    type="text" 
                                    placeholder="Filter components..." 
                                    className="w-full bg-slate-900 border border-slate-700/50 rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all font-medium"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>
                        
                        <div className="px-5 py-3 border-b border-slate-800/80 bg-slate-900/50 font-bold text-xs tracking-widest text-slate-400 uppercase flex items-center justify-between">
                            <span className="flex items-center gap-2"><FolderGit2 size={16} className="text-blue-500" /> Assembly Graph</span>
                            <span className="bg-slate-800 text-slate-300 px-2.5 py-0.5 rounded text-[10px]">{nodesArray.length} parts</span>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto p-3 custom-scrollbar">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center h-full text-slate-500 gap-4">
                                    <div className="w-8 h-8 rounded-full border-4 border-blue-500 border-t-transparent animate-spin"></div>
                                    <span className="font-mono text-sm">Decoding Mesh Architecture...</span>
                                </div>
                            ) : hasError ? (
                                <div className="text-sm text-red-400 p-4 bg-red-500/10 rounded-xl m-2 border border-red-500/20">
                                    <span className="font-bold block mb-1 uppercase tracking-wider text-xs">Architecture Lock</span>
                                    {manifest.details}
                                </div>
                            ) : !nodesArray.length ? (
                                <div className="text-sm text-slate-500 text-center mt-10 font-mono">Empty Assembly Vector</div>
                            ) : (
                                <div className="space-y-0.5">
                                    {displayedNodes.map((node: any) => (
                                        <TreeNode 
                                            key={node.id} 
                                            node={node} 
                                            nodes={nodesArray} 
                                            hiddenNodes={hiddenNodes} 
                                            toggleNodeVisibility={toggleNodeVisibility}
                                            selectedNode={selectedNode}
                                            hoveredNode={hoveredNode}
                                            setSelectedNode={setSelectedNode}
                                            setHoveredNode={setHoveredNode}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Pane: Advanced WebGL Studio */}
                    <div className="flex-1 relative overflow-hidden bg-gradient-to-tr from-[#020617] via-[#0f172a] to-[#1e1b4b]">
                        {/* Interactive Grid Background */}
                        <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-[0.03] pointer-events-none" />
                        <div className="absolute inset-0 bg-blue-500/5 mix-blend-overlay pointer-events-none" />
                        
                        {loading || hasError ? (
                            <div className="absolute inset-0 flex items-center justify-center">
                                {/* Loading state handled in left pane, just keep a nice background here */}
                            </div>
                        ) : (
                            <Canvas shadows camera={{ position: [0, 10, 120], fov: 50 }} dpr={[1, 2]}>
                                <color attach="background" args={['transparent']} />
                                <fog attach="fog" args={['#0f172a', 80, 250]} />
                                
                                {/* Premium Lighting Environment */}
                                <Environment preset="studio" intensity={1.5} />
                                <ambientLight intensity={0.4} />
                                <directionalLight position={[10, 20, 10]} intensity={1.5} castShadow shadow-mapSize={[2048, 2048]} />
                                <directionalLight position={[-10, 20, -10]} intensity={0.5} />
                                
                                <PresentationControls 
                                    global 
                                    config={{ mass: 2, tension: 500 }} 
                                    snap={{ mass: 4, tension: 1500 }} 
                                    rotation={[0, 0.3, 0]} 
                                    polar={[-Math.PI / 3, Math.PI / 3]} 
                                    azimuth={[-Math.PI / 1.4, Math.PI / 2]}
                                >
                                    <Stage environment="city" intensity={0.8} shadows="contact" adjustCamera={false}>
                                        <Bounds fit clip observe margin={1.2}>
                                            <Suspense fallback={null}>
                                                {nodesArray.map((node: any) => {
                                                    const fileStr = node.geometry?.fileUrl; // e.g. meshes/node_0.glb
                                                    if (!fileStr) return null;
                                                    
                                                    const filename = fileStr.split('/').pop();
                                                    const assetUrl = `${API_BASE_URL}/api/v1/genesis/synthesis/${synthesisId}/meshes/${filename}`;
                                                    
                                                    return (
                                                        <IndividualMesh 
                                                            key={node.id} 
                                                            node={node} 
                                                            meshUrl={assetUrl}
                                                            isExploded={isExploded}
                                                            visible={!hiddenNodes.has(node.id)}
                                                            selectedNode={selectedNode}
                                                            hoveredNode={hoveredNode}
                                                            onHover={setHoveredNode}
                                                            onClick={setSelectedNode}
                                                        />
                                                    );
                                                })}
                                            </Suspense>
                                        </Bounds>
                                    </Stage>
                                </PresentationControls>
                                
                                <OrbitControls 
                                    makeDefault 
                                    autoRotate={autoRotate} 
                                    autoRotateSpeed={1.2} 
                                    enableDamping 
                                    dampingFactor={0.05}
                                    maxDistance={300}
                                />
                                {/* Bottom Reflector Plate */}
                                <ContactShadows position={[0, -20, 0]} opacity={0.6} scale={150} blur={2.5} far={40} />
                            </Canvas>
                        )}

                        {/* On-Canvas Telemetry HUD */}
                        <div className="absolute top-6 left-6 pointer-events-none">
                            <div className="flex flex-col gap-2">
                                <div className="bg-black/60 backdrop-blur-md px-4 py-2 border border-slate-700/50 rounded-xl flex items-center justify-between min-w-[220px]">
                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Active Render</span>
                                    <span className="text-sm font-mono text-emerald-400">{nodesArray.length - hiddenNodes.size} / {nodesArray.length}</span>
                                </div>
                                <div className="bg-black/60 backdrop-blur-md px-4 py-2 border border-slate-700/50 rounded-xl flex items-center justify-between">
                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Physics Core</span>
                                    <span className="text-sm font-mono text-blue-400">OpenTURNS</span>
                                </div>
                            </div>
                        </div>

                        {/* Selected Node Details Panel (Gap 3.2b: reads real manifest data) */}
                        {selectedNode && (() => {
                            const selNode = nodesArray.find((n: any) => n.id === selectedNode);
                            if (!selNode) return null;
                            const tolerances = selNode.kinematics?.tolerances || {};
                            const firstTolKey = Object.keys(tolerances)[0];
                            const firstTolVal = firstTolKey ? tolerances[firstTolKey] : '±0.1mm';
                            return (
                                <div className="absolute bottom-6 right-6 bg-slate-900/80 backdrop-blur-xl border border-blue-500/40 rounded-xl p-5 shadow-2xl max-w-[320px] animate-in slide-in-from-right-4 duration-300">
                                    <div className="flex justify-between items-start mb-3">
                                        <h3 className="font-bold text-white text-lg leading-tight">{selNode.name}</h3>
                                        <button onClick={() => setSelectedNode(null)} className="text-slate-400 hover:text-white bg-white/5 rounded-md p-1"><X size={14}/></button>
                                    </div>
                                    <div className="space-y-2 mt-4 text-sm">
                                        <div className="flex justify-between border-b border-slate-800 pb-1">
                                            <span className="text-slate-500">Material Spec</span>
                                            <span className="font-mono text-slate-300 capitalize">{selNode.material_type || selNode.material || 'Unknown'}</span>
                                        </div>
                                        <div className="flex justify-between border-b border-slate-800 pb-1">
                                            <span className="text-slate-500">Mass Est.</span>
                                            <span className="font-mono text-slate-300">{selNode.mass_kg != null ? `${Number(selNode.mass_kg).toFixed(2)} kg` : 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between border-b border-slate-800 pb-1">
                                            <span className="text-slate-500">Π Score</span>
                                            <span className={`font-mono ${(selNode.pi_score || 1.0) < 1.0 ? 'text-red-400' : 'text-emerald-400'}`}>{(selNode.pi_score || 1.0).toFixed(3)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-500">Tolerance ({firstTolKey || 'default'})</span>
                                            <span className="font-mono text-amber-400">{firstTolVal}</span>
                                        </div>
                                        {Object.keys(tolerances).length > 1 && (
                                            <div className="mt-2 pt-2 border-t border-slate-800/60">
                                                <span className="text-[10px] text-slate-500 uppercase tracking-widest">All Constraints</span>
                                                {Object.entries(tolerances).map(([k, v]) => (
                                                    <div key={k} className="flex justify-between text-xs mt-1">
                                                        <span className="text-slate-500 truncate mr-2">{k}</span>
                                                        <span className="font-mono text-slate-400">{v as string}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}
                        
                        {/* Bottom Status Bar */}
                        <div className="absolute bottom-4 left-4 text-[10px] font-mono text-slate-500 tracking-widest uppercase">
                            Magnus Architecture v{manifest?.version || "2.5"} — {synthesisId.slice(0,18)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CadViewerModal;
